import {Component, Inject} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {map, takeUntil} from 'rxjs/operators';
import {ProductPackagesService} from '@app/services/api/product-packages.service';
import {ConfigAPIService} from '@app/services/api/config-api.service';
import {ProductPackage} from '@app/interfaces/packages/product-package';
import {AccountProductPackage} from '@app/interfaces/packages/account-product-package';
import {ProductPackageSubscriptionTypeEnum} from '@app/enums/ProductPackageSubscriptionTypeEnum';
import {NotificationService} from '@app/services/notification.service';
import {AccountConfig} from '@app/interfaces/config/account.config';
import {PaymentStatusResponse} from '@app/services/api/payment.service';

@Component({
  selector: 'app-upgrade-product-package-dialog',
  templateUrl: './upgrade-product-package-dialog.component.html',
  styleUrls: ['./upgrade-product-package-dialog.component.scss'],
  standalone: false
})
export class UpgradeProductPackageDialogComponent {

  public isLoading = true;

  public productPackage: ProductPackage;
  public activePackage: AccountProductPackage;

  public productType: string;

  public readonly productPackageSubscriptionTypeEnum = ProductPackageSubscriptionTypeEnum;

  private destroy$ = new Subject<void>();
  public disableButton: boolean;
  accountConfig$: Observable<AccountConfig>;
  nextYearlyDate: string;
  private accountConfig: AccountConfig;
  // force the trial (no-charge) path even if the trial has lapsed.
  public overrideTrial: boolean;

  // Payment modal state
  public showPaymentModal = false;
  public stripeClientSecret: string | null = null;
  public stripePublishableKey: string = '';
  public stripeDisabled = false; // Account bills manually via Odoo — no card step

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<any>,
    private matDialog: MatDialog,
    private productPackagesService: ProductPackagesService,
    private configApi: ConfigAPIService,
    private notificationService: NotificationService,
  ) {
    this.productPackage = data.productPackage;
    this.productType = data.productType;
    this.overrideTrial = data.overrideTrial || false;
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.accountConfig$ = this.configApi.getAccountConfig();
    this.accountConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.accountConfig = data;
        this.activePackage = data.activeAccountProductPackage;
        this.activePackage.isTrial = this.overrideTrial || this.activePackage.isTrial;
        this.stripePublishableKey = data.stripePublishableKey || '';
        this.stripeDisabled = !!data.stripeDisabled;
        this.updateYearToNextOccurrence();
        this.isLoading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  changeProductPackage() {
    this.disableButton = true;
    this.isLoading = true;

    // trial switch stays a trial (keepTrial=true), no payment/invoice.
    if (this.activePackage.isTrial && (!this.activePackage.isExpired || this.overrideTrial)) {
      this.productPackagesService.changeProductPackage(this.productPackage.uuid, this.productType, true)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.handlePaymentSuccess();
          },
          error: (error) => {
            console.error('Error switching trial package:', error);
            this.notificationService.showErrorAlert(
              error.error?.message || $localize`Napaka pri menjavi paketa`
            );
            this.disableButton = false;
            this.isLoading = false;
          }
        });
      return;
    }

    // Use unified payment flow - checks for existing payment method and creates SetupIntent if needed
    this.productPackagesService.initializePayment(this.productPackage.uuid, this.productType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Check if payment authorization is required
          // (never for Stripe-disabled accounts — they activate without a card)
          if (!this.stripeDisabled && response.requires_authorization && response.stripe_client_secret) {
            // Show Stripe payment modal
            this.stripeClientSecret = response.stripe_client_secret;
            this.showPaymentModal = true;
            this.isLoading = false;
            return;
          }

          // If existing payment method is available, proceed to activate with it
          if (!response.requires_authorization && response.existing_payment_method) {
            this.activateWithExistingPayment();
            return;
          }

          // Package already activated (free package or local development)
          if (response.status === 'activated') {
            this.isLoading = false;
            this.handlePaymentSuccess();
            return;
          }

          // Unexpected state
          this.isLoading = false;
          this.notificationService.showErrorAlert($localize`Nepričakovano stanje aktivacije`);
          this.disableButton = false;
        },
        error: (error) => {
          console.error('Error initializing payment:', error);
          this.notificationService.showErrorAlert(
            error.error?.message || $localize`Napaka pri pripravi plačila`
          );
          this.disableButton = false;
          this.isLoading = false;
        }
      });
  }

  /**
   * Activate product package with existing payment method
   * Called when user already has an authorized card on file
   */
  private activateWithExistingPayment(): void {
    this.productPackagesService.changeProductPackage(this.productPackage.uuid, this.productType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.handlePaymentSuccess();
        },
        error: (error) => {
          console.error('Error activating package:', error);
          this.notificationService.showErrorAlert(
            error.error?.message || $localize`Napaka pri aktivaciji paketa`
          );
          this.disableButton = false;
          this.isLoading = false;
        }
      });
  }

  /**
   * Handle payment authorization completion from Stripe modal
   */
  public onPaymentComplete(response?: PaymentStatusResponse): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.handlePaymentSuccess();
  }

  /**
   * Handle payment cancellation
   */
  public onPaymentCancelled(): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.disableButton = false;
    this.notificationService.showInfoAlert($localize`Avtorizacija plačila preklicana`);
  }

  /**
   * Handle payment failure that requires a new SetupIntent
   * This is called when the SetupIntent enters a terminal state
   */
  public onPaymentFailed(errorMessage: string): void {
    this.showPaymentModal = false;
    this.stripeClientSecret = null;
    this.disableButton = false;
    this.notificationService.showErrorAlert(
      errorMessage || $localize`Avtorizacija plačila ni uspela. Prosimo, poskusite znova.`
    );
  }

  /**
   * Common success handler for both direct activation and payment-authorized activation
   * Note: Proforma dialog removed - Odoo sends invoice automatically when payment is processed
   */
  private handlePaymentSuccess(): void {
    // Refresh account config to get updated package
    this.configApi.getAccountConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.accountConfig = data;
        this.activePackage = data.activeAccountProductPackage;
        this.updateYearToNextOccurrence();
        this.notificationService.showSuccessAlert($localize`Paket uspešno aktiviran!`);
        this.productPackagesService.clearCache();
        this.close(this.productPackage.uuid);
        // Invoice is sent automatically by Odoo when it processes payment
      });
  }

  close(uuid?: string) {
    this.dialogRef.close(uuid);
  }

  static open(dialog: MatDialog, productPackage, productType, overrideTrial = false) {
    return dialog.open(UpgradeProductPackageDialogComponent, {
      width: '400px',
      disableClose: false,
      data: {
        productPackage,
        productType,
        overrideTrial
      }
    });
  }

  updateYearToNextOccurrence() {
    const fromDate = new Date(this.activePackage.billingDate);
    const fromMonth = fromDate.getMonth();
    const fromDay = fromDate.getDate();

    const today = new Date();

// Calculate the next occurrence year
    let nextYear = today.getFullYear();
    if (
      fromMonth < today.getMonth() ||
      (fromMonth === today.getMonth() && fromDay <= today.getDate())
    ) {
      nextYear++; // Increment the year if the day and month have already occurred this year
    }

    fromDate.setFullYear(nextYear);

    this.nextYearlyDate = fromDate.toDateString();

  }
}
